import React, { useState, useRef, useCallback, useEffect } from 'react'
import { apiUploadFile, getErrorMessage } from '../../utils/api'
import { useForwarding } from '../../contexts/ForwardingContext'
import { sameId } from '../../contexts/forwarding'
import { ImageEditor } from './ImageEditor'
import { useVoiceTyping } from '../../hooks/useVoiceTyping'

interface UploadedAttachment {
  id: string
  url: string
  type: 'image' | 'video' | 'file'
  name: string
  size: number
}

interface ChatComposerProps {
  onSendMessage: (content: string, attachments?: UploadedAttachment[]) => Promise<void>
  onSuggestReply: () => Promise<void>
  suggestedReply: string | null
  onAcceptSuggestion: () => Promise<void>
  onDiscardSuggestion: () => Promise<void>
  disabled?: boolean
  /** Called when the agent starts/stops typing so the customer can see a typing indicator */
  onTypingStart?: () => void
  onTypingStop?: () => void
  /** Extra controls rendered in the toolbar next to AI Suggest (e.g. whisper/invite). */
  whisperSlot?: React.ReactNode
  /** This chat's id — used to claim a forwarded Copilot answer destined here. */
  chatId?: string
  /** Narrow layout (e.g. docked beside the co-browse viewer): tidy up the toolbar. */
  compact?: boolean
}

const SAVED_REPLIES = [
  { label: 'Welcome', text: 'Thank you for contacting support!' },
  { label: 'Patience', text: 'We appreciate your patience. Our team will follow up soon.' },
  { label: 'Escalated', text: 'Your issue has been escalated to our technical team.' },
  { label: 'Investigating', text: 'Thank you for providing that information. We are investigating.' },
  { label: 'Known issue', text: 'This appears to be a known issue. A fix is in progress.' },
]

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ALLOWED_EXTENSIONS = '.jpg,.jpeg,.png,.gif,.webp,.svg,.bmp,.tiff,.mp4,.mov,.webm,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip,.rar,.gz,.7z'

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

export const ChatComposer: React.FC<ChatComposerProps> = ({
  onSendMessage,
  onSuggestReply,
  suggestedReply,
  onAcceptSuggestion,
  onDiscardSuggestion,
  disabled = false,
  onTypingStart,
  onTypingStop,
  whisperSlot,
  chatId,
  compact = false,
}) => {
  const [message, setMessage] = useState('')
  const { consume } = useForwarding()
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isTypingRef = useRef(false)

  // Emit typing_start when the agent begins typing, and typing_stop after 3s idle
  const emitTyping = useCallback(() => {
    if (!isTypingRef.current) {
      isTypingRef.current = true
      onTypingStart?.()
    }
    // Reset the idle timer
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    typingTimeoutRef.current = setTimeout(() => {
      isTypingRef.current = false
      onTypingStop?.()
    }, 3000)
  }, [onTypingStart, onTypingStop])

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
      if (isTypingRef.current) onTypingStop?.()
    }
  }, [onTypingStop])
  const [sending, setSending] = useState(false)
  const [showSavedReplies, setShowSavedReplies] = useState(false)
  const [suggesting, setSuggesting] = useState(false)
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([])
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Claim a forwarded Copilot answer destined for THIS chat: prefill + focus,
  // never auto-send. Runs once on mount; consume() returns null (no-op) on a
  // direct load or when the pending forward targets a different chat.
  useEffect(() => {
    const text = consume((t) => t.kind === 'chat' && sameId(t.id, chatId))
    if (text === null) return
    setMessage(text)
    const el = textareaRef.current
    if (el) {
      el.focus()
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 200) + 'px'
      requestAnimationFrame(() => {
        try {
          el.setSelectionRange(text.length, text.length)
        } catch {
          /* ignore */
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Voice typing ──────────────────────────────────────────────────────────
  // Press the mic → talk → text appears live in the box; press again → stop,
  // review/edit like any normal message, then send. Whatever was already typed
  // is kept — dictation appends after it. Button hides where the browser has
  // no speech engine (the mobile app relies on the phone keyboard's native
  // dictation instead).
  const voiceBaseRef = useRef('')
  const messageRef = useRef(message)
  messageRef.current = message
  const voice = useVoiceTyping((finalText, interim) => {
    const combined = (voiceBaseRef.current + (finalText ? finalText + ' ' : '') + interim).replace(/ {2,}/g, ' ')
    setMessage(combined)
    const el = textareaRef.current
    if (el) {
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 200) + 'px'
      el.scrollTop = el.scrollHeight
    }
  })
  const handleVoiceToggle = () => {
    if (!voice.listening) {
      const existing = messageRef.current
      voiceBaseRef.current = existing ? existing.replace(/\s+$/, '') + ' ' : ''
    }
    voice.toggle()
    if (!voice.listening) textareaRef.current?.focus()
  }

  const canSend = (message.trim() || attachments.length > 0) && !disabled && !sending && !uploading

  // Shared upload path for picked files AND pasted/dropped images.
  const uploadFiles = useCallback(async (files: File[]) => {
    if (!files.length) return
    setUploadError(null)
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        setUploadError(`"${file.name || 'image'}" exceeds 10MB limit.`)
        continue
      }
      setUploading(true)
      setUploadProgress(0)
      try {
        // Agent-authenticated upload endpoint. The customer-auth '/upload' twin
        // 401s with an agent token — that was the "upload failed 401".
        const result = await apiUploadFile<UploadedAttachment>('/agent/upload', file, (p) => setUploadProgress(p))
        setAttachments((prev) => [...prev, result])
      } catch (err) {
        setUploadError(getErrorMessage(err, 'Upload failed'))
      } finally {
        setUploading(false)
        setUploadProgress(0)
      }
    }
  }, [])

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    await uploadFiles(Array.from(files))
    // Reset input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // Paste a screenshot / copied image straight into the composer → uploads it as
  // an attachment so it can be sent. Plain-text paste is left untouched.
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items
      if (!items) return
      const images: File[] = []
      for (let i = 0; i < items.length; i++) {
        const it = items[i]
        if (it.kind === 'file' && it.type.startsWith('image/')) {
          const blob = it.getAsFile()
          if (!blob) continue
          // Clipboard images usually have no real filename → synthesise one with
          // the correct extension so the upload + preview behave like a file.
          const ext = (blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg').replace('svg+xml', 'svg')
          const name = blob.name && /\.[a-z0-9]+$/i.test(blob.name) ? blob.name : `pasted-${Date.now()}.${ext}`
          images.push(new File([blob], name, { type: blob.type }))
        }
      }
      if (images.length > 0) {
        e.preventDefault() // don't also paste binary/garbage into the textarea
        void uploadFiles(images)
      }
      // No image in the clipboard → let the normal text paste happen.
    },
    [uploadFiles]
  )

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id))
  }

  // ── Image editor (draw / add text before sending) ──────────────────────────
  const [editing, setEditing] = useState<UploadedAttachment | null>(null)
  const [savingEdit, setSavingEdit] = useState(false)

  // Upload the annotated image and REPLACE the original attachment in place, so
  // the edited version is what gets sent.
  const handleEditSave = useCallback(
    async (blob: Blob) => {
      if (!editing) return
      setSavingEdit(true)
      try {
        const base = (editing.name || 'image').replace(/\.[^.]+$/, '')
        const file = new File([blob], `${base}-edited.png`, { type: 'image/png' })
        const uploaded = await apiUploadFile<UploadedAttachment>('/agent/upload', file, () => {})
        setAttachments((prev) => prev.map((a) => (a.id === editing.id ? uploaded : a)))
        setEditing(null)
      } catch (err) {
        setUploadError(getErrorMessage(err, 'Failed to save the edited image'))
      } finally {
        setSavingEdit(false)
      }
    },
    [editing]
  )

  // Drag & drop files (one or many) anywhere onto the composer → upload + attach.
  const handleDragOver = (e: React.DragEvent) => {
    if (Array.from(e.dataTransfer?.types || []).includes('Files')) {
      e.preventDefault()
      if (!disabled) setIsDragging(true)
    }
  }
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    // Only clear when leaving the composer entirely (not moving between children).
    if (e.currentTarget === e.target) setIsDragging(false)
  }
  const handleDrop = (e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return
    e.preventDefault()
    setIsDragging(false)
    if (disabled) return
    const files = Array.from(e.dataTransfer?.files || [])
    if (files.length) void uploadFiles(files)
  }

  const handleSendMessage = async () => {
    if (!canSend) return
    if (voice.listening) voice.stop() // sending ends the dictation session
    setSending(true)
    try {
      await onSendMessage(message.trim(), attachments.length > 0 ? attachments : undefined)
      setMessage('')
      setAttachments([])
      setUploadError(null)
      // Stop typing indicator immediately on send
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
      if (isTypingRef.current) {
        isTypingRef.current = false
        onTypingStop?.()
      }
      if (textareaRef.current) textareaRef.current.style.height = 'auto'
    } catch (err) {
      console.error('Failed to send message:', err)
    } finally {
      setSending(false)
    }
  }

  const handleSuggest = async () => {
    setSuggesting(true)
    try {
      await onSuggestReply()
    } finally {
      setSuggesting(false)
    }
  }

  const handleAcceptSuggestion = async () => {
    setMessage(suggestedReply || '')
    await onAcceptSuggestion()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Enter sends; Shift+Enter inserts a newline. (IME composition is left alone.)
    if (e.key === 'Enter' && !e.shiftKey && !(e.nativeEvent as any).isComposing) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  // Auto-resize textarea
  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value)
    const el = e.target
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 200) + 'px'
    // Notify widget that the agent is typing
    if (e.target.value.trim()) {
      emitTyping()
    }
  }

  return (
    <div
      className="border-t border-border bg-surface relative"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Image editor — draw / add text on an attached image before sending */}
      {editing && (
        <ImageEditor
          src={editing.url}
          saving={savingEdit}
          onCancel={() => setEditing(null)}
          onSave={handleEditSave}
        />
      )}

      {/* Drag & drop overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-brand-500/10 border-2 border-dashed border-brand-500 rounded-lg m-2 pointer-events-none">
          <span className="text-sm font-medium text-brand-700 dark:text-brand-300">Drop image(s) to attach</span>
        </div>
      )}

      {/* AI Suggestion panel */}
      {suggestedReply && (
        <div className="mx-4 mt-3 p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 animate-slide-up">
          <div className="flex items-start gap-2 mb-2">
            <svg className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
            </svg>
            <div className="flex-1">
              <p className="text-[11px] font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wider mb-1">
                AI Suggestion
              </p>
              <p className="text-sm text-purple-900 dark:text-purple-100 leading-relaxed">
                {suggestedReply}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-6">
            <button
              onClick={handleAcceptSuggestion}
              className="px-2.5 py-1 rounded-md text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 transition-colors"
            >
              Use this
            </button>
            <button
              onClick={() => setMessage(suggestedReply)}
              className="px-2.5 py-1 rounded-md text-xs font-medium text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-800/50 transition-colors"
            >
              Edit first
            </button>
            <button
              onClick={onDiscardSuggestion}
              className="px-2.5 py-1 rounded-md text-xs font-medium text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Saved replies dropdown */}
      {showSavedReplies && (
        <div className="mx-4 mt-2 p-1 rounded-lg border border-border bg-surface-elevated shadow-float animate-slide-up">
          <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary">
            Saved Replies
          </p>
          {SAVED_REPLIES.map((reply, idx) => (
            <button
              key={idx}
              onClick={() => {
                setMessage(reply.text)
                setShowSavedReplies(false)
                textareaRef.current?.focus()
              }}
              className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-surface-hover transition-colors"
            >
              <span className="font-medium text-txt-primary">{reply.label}</span>
              <span className="text-txt-tertiary ml-2 text-xs">{reply.text.slice(0, 50)}...</span>
            </button>
          ))}
        </div>
      )}

      {/* Attachment previews — thumbnails right above the input */}
      {attachments.length > 0 && (
        <div className="mx-4 mt-3 flex flex-wrap gap-2">
          {attachments.map((att) => (
            <div key={att.id} className="relative">
              {att.type === 'image' ? (
                <img
                  src={att.url}
                  alt={att.name}
                  onClick={() => setEditing(att)}
                  title="Click to draw or add text"
                  className="w-16 h-16 rounded-lg object-cover border border-border cursor-pointer hover:opacity-90 transition-opacity"
                />
              ) : (
                <div
                  title={`${att.name} · ${formatFileSize(att.size)}`}
                  className="w-16 h-16 rounded-lg border border-border bg-surface-secondary flex flex-col items-center justify-center gap-1 p-1"
                >
                  {att.type === 'video' ? (
                    <svg className="w-5 h-5 text-txt-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-txt-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                    </svg>
                  )}
                  <span className="text-[9px] text-txt-tertiary truncate max-w-[56px]">{att.name}</span>
                </div>
              )}
              <button
                onClick={() => removeAttachment(att.id)}
                aria-label="Remove attachment"
                title="Remove"
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-900 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload progress bar */}
      {uploading && (
        <div className="mx-4 mt-2">
          <div className="flex items-center justify-between text-xs text-txt-tertiary mb-1">
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Uploading...
            </span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-500 rounded-full transition-all duration-200"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Upload error */}
      {uploadError && (
        <div className="mx-4 mt-2 px-3 py-1.5 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-800 flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
          </svg>
          {uploadError}
          <button onClick={() => setUploadError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Voice-typing status / error */}
      {voice.listening && (
        <div className="mx-4 mt-2 px-3 py-1.5 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-800 flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
          Listening — talk and your words appear below. Press the mic again to stop, then review and send.
        </div>
      )}
      {voice.error && (
        <div className="mx-4 mt-2 px-3 py-1.5 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-800 flex items-center gap-1.5">
          {voice.error}
          <button onClick={voice.clearError} className="ml-auto text-red-400 hover:text-red-600">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Input area */}
      <div className="p-4">
        <div className="rounded-lg border border-border bg-surface-secondary focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500/20 transition-all">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder="Type your reply... (paste a screenshot to attach it)"
            disabled={disabled || sending}
            rows={2}
            // caret-color pinned to the text colour — fixes the caret turning
            // white (invisible) on hover on Windows.
            style={{ caretColor: 'currentColor' }}
            className="w-full px-3.5 py-3 bg-transparent text-sm text-txt-primary caret-current placeholder-txt-tertiary outline-none resize-none min-h-[68px]"
          />

          <div className="flex flex-wrap items-center justify-between gap-y-2 px-3 pb-2.5">
            <div className="flex items-center gap-1">
              {/* Attach */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled || sending || uploading}
                className={`p-1.5 rounded-md transition-colors disabled:opacity-40 ${
                  attachments.length > 0
                    ? 'text-brand-600 bg-brand-50 dark:bg-brand-900/30'
                    : 'text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover'
                }`}
                title="Attach file (max 10MB)"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                </svg>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                accept={ALLOWED_EXTENSIONS}
                onChange={handleFileSelect}
              />

              {/* Voice typing — talk instead of typing; press again to stop */}
              {voice.supported && (
                <button
                  onClick={handleVoiceToggle}
                  disabled={disabled || sending}
                  className={`p-1.5 rounded-md transition-colors disabled:opacity-40 ${
                    voice.listening
                      ? 'text-red-600 bg-red-50 dark:bg-red-900/30 animate-pulse'
                      : 'text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover'
                  }`}
                  title={voice.listening ? 'Stop voice typing' : 'Voice typing — talk and it becomes text'}
                  aria-label={voice.listening ? 'Stop voice typing' : 'Start voice typing'}
                >
                  {voice.listening ? (
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="7" y="7" width="10" height="10" rx="2" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z" />
                    </svg>
                  )}
                </button>
              )}

              {/* Saved replies */}
              <button
                onClick={() => setShowSavedReplies(!showSavedReplies)}
                disabled={disabled || sending}
                className={`p-1.5 rounded-md transition-colors disabled:opacity-40 ${
                  showSavedReplies
                    ? 'text-brand-600 bg-brand-50 dark:bg-brand-900/30'
                    : 'text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover'
                }`}
                title="Saved replies"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776" />
                </svg>
              </button>

              {/* AI suggest */}
              <button
                onClick={handleSuggest}
                disabled={disabled || sending || !!suggestedReply || suggesting}
                className="flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 disabled:opacity-40 transition-colors"
                title="Get AI suggestion"
              >
                <svg className={`w-3.5 h-3.5 ${suggesting ? 'animate-pulse-soft' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                </svg>
                {suggesting ? 'Thinking...' : 'AI Suggest'}
              </button>

              {/* Whisper / invite controls (rendered by ChatThread) */}
              {whisperSlot}
            </div>

            {/* Send button */}
            <div className="flex items-center gap-2 ml-auto">
              <span className={`text-[10px] text-txt-tertiary ${compact ? 'hidden' : 'hidden sm:inline'}`}>
                Enter to send \u00b7 Shift+Enter for new line
              </span>
              <button
                onClick={handleSendMessage}
                disabled={!canSend}
                className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {sending ? (
                  <>
                    <svg className="w-3.5 h-3.5 animate-spin-slow" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending
                  </>
                ) : (
                  <>
                    Send
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
